import { LogOut, Moon, Palette, Settings, Sun } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Coin3D } from "@/components/brand/Logo";
import { WS_HOME, workspaceLinks, workspaceOf } from "@/components/layout/nav";
import { DemoBadge } from "@/components/common/DemoBadge";
import { CompanyLogo } from "@/components/business/CompanyLogo";
import { HouseholdSwitcher } from "@/components/layout/HouseholdSwitcher";
import { CompanySwitcher } from "@/components/layout/CompanySwitcher";
import { NotificationsBell } from "@/components/layout/NotificationsBell";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

const iconBtn = "flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card transition-colors hover:bg-secondary";

export const TopBar = () => {
  const { user, logout } = useAuth();
  const { resolved, setTheme } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ws = workspaceOf(user, pathname);
  const spaces = workspaceLinks(user);
  const wsLabel = ws === "admin" ? "Console CASHMY" : ws === "partner" ? user.partner?.company : ws === "business" ? user.companies?.[0]?.name || "CASHMY Entreprise" : user.organizations[0]?.name;
  const initials = user.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header className="glass sticky top-0 z-30 border-b" data-testid="top-bar">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-3 px-4 sm:px-6 lg:px-10">
        <Link to={WS_HOME[ws]} aria-label="Accueil" className="md:hidden" data-testid="topbar-logo-home"><Coin3D size={42} /></Link>
        {ws === "personal" ? <HouseholdSwitcher /> : ws === "business" && user.companies?.length ? <CompanySwitcher /> : (
          <span className="flex max-w-[12rem] items-center gap-2 truncate rounded-full border border-border bg-card px-3 py-1.5 text-sm font-medium sm:max-w-xs" data-testid="workspace-label">{ws === "business" && <CompanyLogo company={user.companies?.[0]} className="h-5 w-5" />}<span className="truncate">{wsLabel}</span></span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {user.is_demo && <DemoBadge className="hidden sm:inline-flex" />}
          <button className={iconBtn} onClick={() => setTheme(resolved === "dark" ? "light" : "dark")} aria-label="Changer de thème" data-testid="theme-toggle-button">
            {resolved === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <NotificationsBell className={iconBtn} />
          <DropdownMenu>
            <DropdownMenuTrigger className="flex h-9 w-9 items-center justify-center rounded-full bg-navy text-xs font-bold text-[#F8FAF8] dark:bg-mint dark:text-navy" data-testid="user-menu-button">
              {initials}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>
                <div className="truncate text-sm">{user.name}</div>
                <div className="truncate text-xs font-normal text-muted-foreground">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {spaces.length > 1 && spaces.map((s) => (
                <DropdownMenuItem key={s.key} onClick={() => navigate(s.to)} data-testid={`user-menu-${s.key}`}><s.icon className="mr-2 h-4 w-4" />{s.label}</DropdownMenuItem>
              ))}
              {["admin", "demo_admin"].includes(user.platform_role) && <DropdownMenuItem onClick={() => navigate("/brand")} data-testid="user-menu-brand"><Palette className="mr-2 h-4 w-4" />Identité visuelle</DropdownMenuItem>}
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="user-menu-settings"><Settings className="mr-2 h-4 w-4" />Réglages</DropdownMenuItem>
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="user-menu-logout"><LogOut className="mr-2 h-4 w-4" />Se déconnecter</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
